import { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';

interface CalculatorResults {
  termLifeMonthly: number;
  wholeLifeMonthly: number;
  monthlySavings: number;
  twentyYearSavings: number;
}

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  smsConsent: boolean;
}

const PremiumCalculator = () => {
  const [age, setAge] = useState(35);
  const [coverage, setCoverage] = useState(500000);
  const [termLength, setTermLength] = useState(20);
  const [annualIncome, setAnnualIncome] = useState(75000);
  const [incomeMultiplier, setIncomeMultiplier] = useState(10);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [leadFormData, setLeadFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    smsConsent: false,
  });
  const [results, setResults] = useState<CalculatorResults>({
    termLifeMonthly: 0,
    wholeLifeMonthly: 0,
    monthlySavings: 0,
    twentyYearSavings: 0,
  });

  // Calculate recommended coverage based on income
  const recommendedCoverage = annualIncome * incomeMultiplier;

  // Rate calculation logic based on age bands and coverage tiers
  const calculatePremiums = (selectedAge: number, selectedCoverage: number, selectedTerm: number) => {
    // Base rate per $1,000 of coverage for term life
    let termBaseRate = 0;
    
    // Age-based rate tiers
    if (selectedAge >= 18 && selectedAge <= 30) {
      termBaseRate = 0.06;
    } else if (selectedAge >= 31 && selectedAge <= 40) {
      termBaseRate = 0.08;
    } else if (selectedAge >= 41 && selectedAge <= 50) {
      termBaseRate = 0.15;
    } else if (selectedAge >= 51 && selectedAge <= 60) {
      termBaseRate = 0.35;
    } else if (selectedAge >= 61 && selectedAge <= 70) {
      termBaseRate = 0.75;
    }

    // Term length multiplier
    const termMultiplier = selectedTerm === 10 ? 0.8 : selectedTerm === 20 ? 1.0 : 1.15;
    
    // Calculate term life monthly premium
    const termMonthly = (selectedCoverage / 1000) * termBaseRate * termMultiplier;

    // Whole life is typically 8-12x more expensive than term
    const wholeLifeMultiplier = selectedAge < 40 ? 10 : selectedAge < 55 ? 9 : 8;
    const wholeMonthly = termMonthly * wholeLifeMultiplier;

    // Calculate savings
    const monthly = wholeMonthly - termMonthly;
    const twentyYear = monthly * 12 * 20;

    setResults({
      termLifeMonthly: Math.round(termMonthly * 100) / 100,
      wholeLifeMonthly: Math.round(wholeMonthly * 100) / 100,
      monthlySavings: Math.round(monthly * 100) / 100,
      twentyYearSavings: Math.round(twentyYear),
    });
  };

  useEffect(() => {
    calculatePremiums(age, coverage, termLength);
  }, [age, coverage, termLength]);

  const coverageOptions = [
    { value: 100000, label: '$100,000' },
    { value: 250000, label: '$250,000' },
    { value: 500000, label: '$500,000' },
    { value: 750000, label: '$750,000' },
    { value: 1000000, label: '$1,000,000' },
    { value: 1500000, label: '$1,500,000' },
    { value: 2000000, label: '$2,000,000' },
  ];

  const termOptions = [10, 20, 30];

  // Generate chart data for visual comparison
  const generateChartData = () => {
    const years = [5, 10, 15, 20];
    return years.map(year => ({
      year,
      termTotal: results.termLifeMonthly * 12 * year,
      wholeTotal: results.wholeLifeMonthly * 12 * year,
    }));
  };

  const chartData = generateChartData();
  const maxValue = Math.max(...chartData.map(d => d.wholeTotal));

  // Handle lead form submission
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Insert lead into Supabase
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .insert({
          name: leadFormData.name,
          email: leadFormData.email,
          phone: leadFormData.phone,
          source: 'Term Life Calculator',
          status: 'new',
          lead_type: 'calculator',
          notes: `Calculator inputs: Age ${age}, Coverage $${coverage.toLocaleString()}, Term ${termLength} years, Estimated Premium $${results.termLifeMonthly}/mo`,
          sms_consent: leadFormData.smsConsent,
        })
        .select()
        .maybeSingle();

      if (leadError) throw leadError;

      // Store calculator results
      if (leadData) {
        await supabase.from('calculator_results').insert({
          lead_id: leadData.id,
          calculator_type: 'term_life_premium',
          inputs: {
            age,
            coverage,
            termLength,
            annualIncome,
            incomeMultiplier,
          },
          results: {
            termLifeMonthly: results.termLifeMonthly,
            wholeLifeMonthly: results.wholeLifeMonthly,
            monthlySavings: results.monthlySavings,
            twentyYearSavings: results.twentyYearSavings,
          },
        });
      }

      // Trigger email notification via edge function
      await supabase.functions.invoke('send-email', {
        body: {
          to: leadFormData.email,
          subject: 'Your Term Life Insurance Quote Estimate',
          html: `
            <h2>Thank you for using our Term Life Calculator!</h2>
            <p>Hi ${leadFormData.name},</p>
            <p>Based on your inputs, here's your personalized estimate:</p>
            <ul>
              <li><strong>Age:</strong> ${age}</li>
              <li><strong>Coverage Amount:</strong> $${coverage.toLocaleString()}</li>
              <li><strong>Term Length:</strong> ${termLength} years</li>
              <li><strong>Estimated Monthly Premium:</strong> $${results.termLifeMonthly}</li>
            </ul>
            <p>You could save <strong>$${results.monthlySavings}/month</strong> compared to whole life insurance!</p>
            <p>One of our licensed agents will contact you within 24 hours to provide your exact quote and answer any questions.</p>
            <p>Best regards,<br>Hoosier Life Insurance Team</p>
          `,
        },
      });

      setSubmitSuccess(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('There was an error submitting your information. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-[#0a1628]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Term Life Premium Calculator
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See how much you could save with term life insurance compared to whole life. 
            Adjust the inputs below to get your personalized estimate.
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-12">
          {/* Coverage Needs Estimator */}
          <div className="bg-gradient-to-br from-[#D97642]/10 to-transparent border border-[#D97642]/20 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <i className="ri-calculator-line text-[#D97642]"></i>
              Coverage Needs Estimator
            </h3>
            <p className="text-gray-300 mb-6 text-sm">
              Financial experts recommend coverage equal to 10-15x your annual income to protect your family's financial future.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white font-semibold mb-3 text-sm">
                  Annual Income
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
                  <input
                    type="number"
                    value={annualIncome}
                    onChange={(e) => setAnnualIncome(Number(e.target.value))}
                    className="w-full pl-8 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                    min="0"
                    step="5000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white font-semibold mb-3 text-sm">
                  Income Multiplier
                </label>
                <div className="flex gap-2">
                  {[8, 10, 12, 15].map((mult) => (
                    <button
                      key={mult}
                      onClick={() => setIncomeMultiplier(mult)}
                      className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all whitespace-nowrap text-sm ${
                        incomeMultiplier === mult
                          ? 'bg-[#D97642] text-white shadow-lg'
                          : 'bg-white/10 text-gray-300 hover:bg-white/15 border border-white/20'
                      }`}
                    >
                      {mult}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-sm text-gray-400">Recommended Coverage:</p>
              <p className="text-2xl font-bold text-[#D97642]">
                ${recommendedCoverage.toLocaleString()}
              </p>
              <button
                onClick={() => {
                  const closest = coverageOptions.reduce((prev, curr) => 
                    Math.abs(curr.value - recommendedCoverage) < Math.abs(prev.value - recommendedCoverage) ? curr : prev
                  );
                  setCoverage(closest.value);
                }}
                className="mt-2 text-sm text-[#D97642] hover:text-[#C26638] font-medium whitespace-nowrap"
              >
                Apply this amount <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>

          {/* Input Controls */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Age Slider */}
            <div>
              <label className="block text-white font-semibold mb-3">
                Your Age
              </label>
              <div className="space-y-4">
                <input
                  type="range"
                  min="18"
                  max="70"
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#D97642]"
                  style={{
                    background: `linear-gradient(to right, #D97642 0%, #D97642 ${((age - 18) / 52) * 100}%, rgba(255,255,255,0.2) ${((age - 18) / 52) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-gray-400">
                  <span>18</span>
                  <span className="text-2xl font-bold text-[#D97642]">{age}</span>
                  <span>70</span>
                </div>
              </div>
            </div>

            {/* Coverage Amount Dropdown */}
            <div>
              <label className="block text-white font-semibold mb-3">
                Coverage Amount
              </label>
              <select
                value={coverage}
                onChange={(e) => setCoverage(Number(e.target.value))}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-lg font-semibold cursor-pointer hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97642]"
              >
                {coverageOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#0a1628] text-white">
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Term Length Radio Buttons */}
            <div>
              <label className="block text-white font-semibold mb-3">
                Term Length
              </label>
              <div className="flex gap-3">
                {termOptions.map((term) => (
                  <button
                    key={term}
                    onClick={() => setTermLength(term)}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all whitespace-nowrap ${
                      termLength === term
                        ? 'bg-[#D97642] text-white shadow-lg shadow-[#D97642]/30'
                        : 'bg-white/10 text-gray-300 hover:bg-white/15 border border-white/20'
                    }`}
                  >
                    {term} Years
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results Display */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Term Life Premium */}
            <div className="bg-gradient-to-br from-[#D97642]/20 to-[#D97642]/5 border border-[#D97642]/30 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-shield-check-line text-[#D97642] text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Term Life Premium</h3>
              </div>
              <p className="text-3xl font-bold text-white">
                ${results.termLifeMonthly.toFixed(2)}
                <span className="text-base font-normal text-gray-400">/mo</span>
              </p>
            </div>

            {/* Whole Life Premium */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-heart-pulse-line text-gray-400 text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Whole Life Premium</h3>
              </div>
              <p className="text-3xl font-bold text-white">
                ${results.wholeLifeMonthly.toFixed(2)}
                <span className="text-base font-normal text-gray-400">/mo</span>
              </p>
            </div>

            {/* Monthly Savings */}
            <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-money-dollar-circle-line text-green-400 text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Monthly Savings</h3>
              </div>
              <p className="text-3xl font-bold text-green-400">
                ${results.monthlySavings.toFixed(2)}
              </p>
            </div>

            {/* 20-Year Total Savings */}
            <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-line-chart-line text-green-400 text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">20-Year Savings</h3>
              </div>
              <p className="text-3xl font-bold text-green-400">
                ${results.twentyYearSavings.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Visual Cost Comparison Chart */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-[#D97642]"></i>
              Cost Comparison Over Time
            </h3>
            <div className="space-y-6">
              {chartData.map((data, index) => (
                <div key={data.year}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-300">{data.year} Years</span>
                    <span className="text-sm text-gray-400">
                      Save ${(data.wholeTotal - data.termTotal).toLocaleString()}
                    </span>
                  </div>
                  <div className="relative h-12 bg-white/5 rounded-lg overflow-hidden">
                    {/* Term Life Bar */}
                    <div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#D97642] to-[#C26638] flex items-center justify-end pr-3 transition-all duration-500"
                      style={{ width: `${(data.termTotal / maxValue) * 100}%` }}
                    >
                      <span className="text-xs font-bold text-white whitespace-nowrap">
                        ${data.termTotal.toLocaleString()}
                      </span>
                    </div>
                    {/* Whole Life Bar (outline) */}
                    <div
                      className="absolute left-0 top-0 h-full border-2 border-dashed border-gray-400 flex items-center justify-end pr-3 transition-all duration-500"
                      style={{ width: `${(data.wholeTotal / maxValue) * 100}%` }}
                    >
                      <span className="text-xs font-bold text-gray-300 whitespace-nowrap">
                        ${data.wholeTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-r from-[#D97642] to-[#C26638] rounded"></div>
                <span className="text-sm text-gray-300">Term Life</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-dashed border-gray-400 rounded"></div>
                <span className="text-sm text-gray-300">Whole Life</span>
              </div>
            </div>
          </div>

          {/* Lead Capture Form or CTA */}
          {!showLeadForm && !submitSuccess && (
            <div className="text-center space-y-4">
              <button
                onClick={() => setShowLeadForm(true)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] hover:bg-[#C26638] text-white font-semibold rounded-lg transition-colors shadow-lg shadow-[#D97642]/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line text-xl"></i>
                Get My Exact Quote
              </button>
              <p className="text-sm text-gray-400 max-w-2xl mx-auto">
                <i className="ri-information-line mr-1"></i>
                These are estimated rates for illustrative purposes only. Actual premiums vary based on 
                health status, lifestyle factors, carrier, and underwriting.
              </p>
            </div>
          )}

          {/* Lead Capture Form */}
          {showLeadForm && !submitSuccess && (
            <div className="bg-gradient-to-br from-[#D97642]/10 to-transparent border border-[#D97642]/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-2 text-center">
                Get Your Personalized Quote
              </h3>
              <p className="text-gray-300 mb-6 text-center">
                A licensed agent will contact you within 24 hours with your exact quote
              </p>
              <form onSubmit={handleLeadSubmit} className="space-y-4 max-w-2xl mx-auto">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={leadFormData.name}
                      onChange={(e) => setLeadFormData({ ...leadFormData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={leadFormData.email}
                      onChange={(e) => setLeadFormData({ ...leadFormData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white font-medium mb-2 text-sm">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={leadFormData.phone}
                    onChange={(e) => setLeadFormData({ ...leadFormData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                    placeholder="(317) 555-0123"
                  />
                </div>
                <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                  <input
                    type="checkbox"
                    id="smsConsent"
                    checked={leadFormData.smsConsent}
                    onChange={(e) => setLeadFormData({ ...leadFormData, smsConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#D97642] cursor-pointer"
                  />
                  <label htmlFor="smsConsent" className="text-sm text-gray-300 cursor-pointer">
                    I consent to receive text messages about my quote and policy information. Message and data rates may apply.
                  </label>
                </div>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowLeadForm(false)}
                    className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg transition-colors border border-white/20 whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-[#D97642] hover:bg-[#C26638] text-white font-semibold rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-fill"></i>
                        Submit Request
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Success Message */}
          {submitSuccess && (
            <div className="bg-gradient-to-br from-green-500/20 to-transparent border border-green-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-4xl text-green-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Quote Request Received!
              </h3>
              <p className="text-gray-300 mb-6">
                Thank you for your interest! We've sent your estimate to <strong>{leadFormData.email}</strong>.
                A licensed agent will contact you within 24 hours to provide your exact quote.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/book-appointment"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D97642] hover:bg-[#C26638] text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-calendar-line"></i>
                  Schedule a Call Now
                </a>
                <button
                  onClick={() => {
                    setSubmitSuccess(false);
                    setLeadFormData({ name: '', email: '', phone: '', smsConsent: false });
                  }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg transition-colors border border-white/20 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calculator-line"></i>
                  Calculate Another Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PremiumCalculator;